import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type {
  CommandAuditEntry,
  CommandEvent,
  CommandItem,
  ExecuteCommandInput,
  NotRightReason,
} from "@ceo-cmd/shared";
import { ExternalLink, Sparkles } from "lucide-react";
import { getTodayItemAudit, getTodayItemEvents } from "@/api/today";
import {
  askAiHref,
  buildDelegateInput,
  laterRemindAt,
  NOT_RIGHT_REASONS,
  RETURN_CONDITIONS,
  type ReturnCondition,
} from "./action-helpers";
import { DecisionHistory } from "./decision-history";
import { dueLabel, formatMoney } from "./labels";

export function CommandItemPanel({
  item,
  busy,
  onExecute,
}: {
  item: CommandItem | null;
  busy?: boolean;
  onExecute: (body: ExecuteCommandInput) => void;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [audit, setAudit] = useState<CommandAuditEntry[]>([]);
  const [events, setEvents] = useState<CommandEvent[]>([]);
  const [auditLoaded, setAuditLoaded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [delegateOpen, setDelegateOpen] = useState(false);
  const [laterOpen, setLaterOpen] = useState(false);
  const [notRightOpen, setNotRightOpen] = useState(false);
  const [assignee, setAssignee] = useState("Omar");
  const [instruction, setInstruction] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [returnCondition, setReturnCondition] =
    useState<ReturnCondition>("When completed");
  const [customDate, setCustomDate] = useState("");

  useEffect(() => {
    setDetailsOpen(false);
    setAudit([]);
    setEvents([]);
    setAuditLoaded(false);
    setConfirmOpen(false);
    setDelegateOpen(false);
    setLaterOpen(false);
    setNotRightOpen(false);
    setInstruction("");
  }, [item?.id]);

  useEffect(() => {
    if (!detailsOpen || !item || auditLoaded) return;
    void Promise.all([
      getTodayItemEvents(item.id).catch(() => ({ events: [] as CommandEvent[] })),
      getTodayItemAudit(item.id).catch(() => ({
        audit: [] as CommandAuditEntry[],
      })),
    ]).then(([eventsRes, auditRes]) => {
      setEvents(eventsRes.events);
      setAudit(auditRes.audit);
      setAuditLoaded(true);
    });
  }, [detailsOpen, item, auditLoaded]);

  if (!item) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-border bg-card/40 p-6 text-center text-sm text-muted-foreground">
        Select a command item to decide.
      </div>
    );
  }

  function runPrimary() {
    if (
      item!.actionDefinition.confirmationRequired ||
      item!.type === "ACKNOWLEDGE"
    ) {
      setConfirmOpen(true);
      return;
    }
    onExecute({ disposition: "execute" });
  }

  function handleNotRight(reason: NotRightReason) {
    if (reason === "wrong_timing") {
      setNotRightOpen(false);
      setLaterOpen(true);
      return;
    }
    if (reason === "wrong_owner") {
      setNotRightOpen(false);
      setDelegateOpen(true);
      return;
    }
    onExecute({ disposition: "not_right", notRightReason: reason });
    setNotRightOpen(false);
  }

  const distinctAlternatives = item.alternatives.filter(
    (alternative) =>
      alternative.disposition === "execute" &&
      alternative.label.toLocaleLowerCase() !==
        item.actionDefinition.actionLabel.toLocaleLowerCase(),
  );

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card">
      <div className="border-b border-border px-4 py-3 lg:px-5 lg:py-4 xl:px-6">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Recommended move
        </p>
        <h2 className="mt-1 text-lg font-semibold leading-snug text-foreground xl:text-xl">
          {item.recommendation}
        </h2>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 lg:space-y-5 lg:px-5 lg:py-5 xl:px-6">
        <section>
          <button
            type="button"
            disabled={busy}
            onClick={runPrimary}
            className="w-full rounded-md bg-brand px-3 py-2.5 text-sm font-medium text-brand-foreground hover:opacity-90 disabled:opacity-50"
          >
            {item.actionDefinition.actionLabel}
          </button>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            {dueLabel(item.dueAt ?? item.reviewAt)} · {item.priority}
            {item.financialExposure != null
              ? ` · ${formatMoney(item.financialExposure, item.currency)} exposure`
              : ""}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Secondary
              label="Delegate"
              disabled={busy}
              onClick={() => setDelegateOpen(true)}
            />
            <Secondary
              label="Later"
              disabled={busy}
              onClick={() => setLaterOpen(true)}
            />
            <Secondary
              label="Not right"
              disabled={busy}
              onClick={() => setNotRightOpen(true)}
            />
            <Link
              to={askAiHref(item)}
              className="inline-flex items-center justify-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs font-medium text-foreground hover:bg-surface-hover"
            >
              <Sparkles className="size-3" />
              Ask AI
            </Link>
          </div>
        </section>

        {confirmOpen && (
          <ConfirmBlock
            item={item}
            busy={busy}
            onCancel={() => setConfirmOpen(false)}
            onConfirm={() => {
              setConfirmOpen(false);
              onExecute({ disposition: "execute" });
            }}
          />
        )}

        {delegateOpen && (
          <div className="space-y-2 rounded-md border border-border bg-background p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Delegate
            </p>
            <input
              className="w-full rounded border border-border bg-bg px-2 py-1.5 text-sm"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              placeholder="Person"
            />
            <textarea
              className="w-full rounded border border-border bg-bg px-2 py-1.5 text-sm"
              rows={2}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="Short instruction"
            />
            <input
              type="date"
              className="w-full rounded border border-border bg-bg px-2 py-1.5 text-sm"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
            <select
              className="w-full rounded border border-border bg-bg px-2 py-1.5 text-sm"
              value={returnCondition}
              onChange={(e) =>
                setReturnCondition(e.target.value as ReturnCondition)
              }
            >
              {RETURN_CONDITIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <div className="flex gap-2">
              <button
                type="button"
                className="flex-1 rounded-md border border-border px-2 py-1.5 text-xs"
                onClick={() => setDelegateOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                className="flex-1 rounded-md bg-brand px-2 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
                onClick={() => {
                  onExecute(
                    buildDelegateInput({
                      assignee,
                      instruction,
                      dueDate,
                      returnCondition,
                    }),
                  );
                  setDelegateOpen(false);
                }}
              >
                Confirm delegate
              </button>
            </div>
          </div>
        )}

        {laterOpen && (
          <div className="space-y-2 rounded-md border border-border bg-background p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Later
            </p>
            {(
              [
                ["today", "Later today"],
                ["tomorrow", "Tomorrow"],
                ["next_business_day", "Next business day"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                disabled={busy}
                className="w-full rounded-md border border-border px-2.5 py-2 text-left text-sm hover:bg-surface-hover disabled:opacity-50"
                onClick={() => {
                  onExecute({
                    disposition: "later",
                    remindAt: laterRemindAt(key),
                  });
                  setLaterOpen(false);
                }}
              >
                {label}
              </button>
            ))}
            <div className="flex gap-2">
              <input
                type="date"
                className="flex-1 rounded border border-border bg-bg px-2 py-1.5 text-sm"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
              />
              <button
                type="button"
                disabled={busy || !customDate}
                className="rounded-md border border-border px-2.5 py-1.5 text-xs disabled:opacity-50"
                onClick={() => {
                  onExecute({
                    disposition: "later",
                    remindAt: laterRemindAt("date", customDate),
                  });
                  setLaterOpen(false);
                }}
              >
                Choose date
              </button>
            </div>
            <button
              type="button"
              className="w-full text-xs text-muted-foreground hover:underline"
              onClick={() => setLaterOpen(false)}
            >
              Cancel
            </button>
          </div>
        )}

        {notRightOpen && (
          <div className="space-y-1.5 rounded-md border border-border bg-background p-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Not right
            </p>
            {NOT_RIGHT_REASONS.map((reason) => (
              <button
                key={reason.id}
                type="button"
                disabled={busy}
                className="w-full rounded-md border border-border px-2.5 py-2 text-left text-sm hover:bg-surface-hover disabled:opacity-50"
                onClick={() => handleNotRight(reason.id)}
              >
                {reason.label}
              </button>
            ))}
            <button
              type="button"
              className="w-full pt-1 text-xs text-muted-foreground hover:underline"
              onClick={() => setNotRightOpen(false)}
            >
              Cancel
            </button>
          </div>
        )}

        <Section title="Situation">{item.situationSummary}</Section>
        <Section title="Why now">{item.whyInToday}</Section>
        <Section title="Expected result">{item.expectedOutcome}</Section>
        <Section title="Risk of waiting">{item.riskOfInaction}</Section>

        {distinctAlternatives.length > 0 && (
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Alternatives
            </h3>
            <div className="mt-2 flex flex-col gap-1.5">
              {distinctAlternatives.map((alt) => (
                <button
                  key={alt.id}
                  type="button"
                  disabled={busy}
                  onClick={() => onExecute({ disposition: alt.disposition })}
                  className="rounded-md border border-border px-2.5 py-2 text-left text-sm hover:bg-surface-hover disabled:opacity-50"
                >
                  {alt.label}
                </button>
              ))}
            </div>
          </section>
        )}

        <section>
          <button
            type="button"
            onClick={() => setDetailsOpen((v) => !v)}
            aria-expanded={detailsOpen}
            className="text-xs font-medium text-brand hover:underline"
          >
            {detailsOpen
              ? "Hide details and evidence"
              : "Details and evidence"}
          </button>
          {detailsOpen && (
            <div className="mt-2 space-y-3">
              {item.evidence.length > 0 && (
                <div>
                  <p className="mb-1 text-[11px] font-medium uppercase text-muted-foreground">
                    Supporting signals
                  </p>
                  <ul className="space-y-1.5">
                    {item.evidence.map((s) => (
                      <li
                        key={s.id}
                        className="rounded-md border border-border px-2.5 py-2 text-sm"
                      >
                        <p className="text-foreground">{s.label}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {s.source} ·{" "}
                          {new Date(s.occurredAt).toLocaleDateString()}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {item.sourceLinks.length > 0 && (
                <div>
                  <p className="mb-1 text-[11px] font-medium uppercase text-muted-foreground">
                    Source records
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {item.sourceLinks.map((link) => (
                      <a
                        key={link.url}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-surface-hover"
                      >
                        {link.label}
                        <ExternalLink className="size-3" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                <p className="font-medium uppercase tracking-wide">
                  Raw task summary
                </p>
                <p className="mt-1">
                  {item.sourceTaskCount} source tasks ·{" "}
                  {item.suppressedTaskCount} suppressed
                </p>
              </div>
              <div className="text-xs text-muted-foreground">
                <p className="font-medium uppercase tracking-wide">
                  AI reasoning
                </p>
                <p className="mt-1">
                  Priority score {item.priorityScore} · confidence{" "}
                  {Math.round(item.confidence * 100)}% · data refreshed{" "}
                  {new Date(item.dataFreshness).toLocaleString()}
                </p>
              </div>
              {auditLoaded && (
                <DecisionHistory events={events} auditFallback={audit} />
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function ConfirmBlock({
  item,
  busy,
  onConfirm,
  onCancel,
}: {
  item: CommandItem;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
      <p className="font-medium text-foreground">
        {item.actionDefinition.actionLabel}?
      </p>
      {item.type === "ACKNOWLEDGE" ? (
        <AcknowledgementDetails item={item} />
      ) : (
        <p className="text-xs text-muted-foreground">
          This will update {item.companyName} in CEO Command
          {item.actionDefinition.reversible
            ? " (Undo available shortly)."
            : "."}
        </p>
      )}
      <ul className="list-inside list-disc text-xs text-muted-foreground">
        <li>Local decision recorded in CEO Command</li>
        {item.actionDefinition.externalSystemWrites.map((w) => (
          <li key={`${w.system}-${w.description}`}>
            {w.description}: simulated
          </li>
        ))}
        {item.actionDefinition.sideEffects
          .filter(
            (s) =>
              !item.actionDefinition.externalSystemWrites.some((w) =>
                w.description.includes(s),
              ),
          )
          .map((s) => (
            <li key={s}>{s}</li>
          ))}
      </ul>
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          className="flex-1 rounded-md border border-border px-2 py-1.5 text-xs"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={busy}
          className="flex-1 rounded-md bg-brand px-2 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
          onClick={onConfirm}
        >
          Confirm
        </button>
      </div>
    </div>
  );
}

function AcknowledgementDetails({ item }: { item: CommandItem }) {
  const owner = item.ownerName ?? "Group Treasury";
  const escalationAt =
    item.actionDefinition.nextReviewDate ??
    new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString();
  const escalationLabel = new Date(escalationAt).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
  });

  return (
    <>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md border border-border p-2 text-xs">
        <dt className="text-muted-foreground">Finance owner</dt>
        <dd className="font-medium text-foreground">{owner}</dd>
        <dt className="text-muted-foreground">Escalate on</dt>
        <dd className="font-medium text-foreground">{escalationLabel}</dd>
        <dt className="text-muted-foreground">Return to CEO if</dt>
        <dd className="font-medium text-foreground">
          Unresolved by escalation date
        </dd>
      </dl>
      <p className="text-xs text-muted-foreground">
        This will record your acknowledgement, assign continued ownership to{" "}
        {owner}, move the item to Waiting, and return it to Today on{" "}
        {escalationLabel} if unresolved.
      </p>
    </>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      <div className="mt-1 text-sm text-foreground">{children}</div>
    </section>
  );
}

function Secondary({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="rounded-md border border-border px-2 py-1.5 text-xs font-medium text-foreground hover:bg-surface-hover disabled:opacity-50"
    >
      {label}
    </button>
  );
}
